import type { IContentItemsContainer } from '@kontent-ai/delivery-sdk'
import {
  contentTypes,
  type NavigationItemModel,
  type NavigationSectionModel,
  type ScriptTagModel,
  type SiteConfigurationModel
} from '~/models'
import type { KontentPageRoute } from '~/types/KontentPageRoute'
import type { NavigationItem } from '~/types/NavigationItem'

interface SiteState {
  site: SiteConfigurationModel
  fetched: boolean
  linkedItems: IContentItemsContainer
  pages: KontentPageRoute[]
}

export const useSiteStore = defineStore('site', {
  state: (): SiteState => ({
    site: {} as SiteConfigurationModel,
    fetched: false,
    linkedItems: {} as IContentItemsContainer,
    pages: [] as KontentPageRoute[]
  }),
  actions: {
    async fetchData() {
      if (!this.fetched) {
        const response = await $fetch('/api/site-configuration')
        const pagesResponse = await $fetch('/api/navigation/pages')
        if (response && pagesResponse) {
          //@ts-ignore
          this.site = response?.data?.item as SiteConfigurationModel
          //@ts-ignore
          this.linkedItems = response?.data?.linkedItems as IContentItemsContainer
          this.pages = ((pagesResponse?.data as unknown) as KontentPageRoute[] | undefined) ?? []
          this.fetched = true
        }
      }
    }
  },
  getters: {
    scriptTags: (state) => {
      const tags: ScriptTagModel[] = []

      if (state.fetched) {
        for (const item of state.site.elements.scriptTags.value) {
          const linkedItem = state.linkedItems?.[item] as ScriptTagModel
          if (!linkedItem) continue
          tags.push(linkedItem)
        }
      }

      return tags
    },
    getLinkedItem:
      (state) =>
        <T>(codename: string) => {
          return state.linkedItems?.[codename] as T
        },
    getUrlByCodename: (state) => (codename: string) => {
      if (!state.fetched || !state.pages) return ''
      const page = state.pages?.find((x) => x?.codename === codename)
      return page?.path ?? ''
    },
    getUrlByItemId: (state) => (itemId: string) => {
      if (!state.fetched || !state.pages) return ''
      const page = state.pages?.find((x) => x?.itemid === itemId)
      return page?.path ?? ''
    },
    getPage: (state) => (urlPath: string, itemId?: string | null) => {
      if (!state.fetched || !state.pages) return null
      if (itemId) {
        return state.pages?.find((x) => x?.itemid === itemId) ?? null
      }
      return state.pages?.find((x) => x?.path === urlPath) ?? null
    },
    getHeaderNavigationItems: (state) => (): NavigationItem[] => {
      if (!state.fetched) return []

      const navigationItems: NavigationItem[] = []

      if (state.fetched && state.site) {
        const headerCodename = state.site?.elements?.navigationHeader?.value?.[0]
        if (!headerCodename) return []
        const navigation = state.linkedItems?.[headerCodename] as NavigationSectionModel | undefined
        if (!navigation) return []

        for (const item of navigation?.elements?.navigationItems?.value ?? []) {

          const linkedItem = state.linkedItems?.[item] as NavigationItemModel | NavigationSectionModel
          if (!linkedItem) continue

          let url = ''

          if (linkedItem.system.type == contentTypes.navigation_item.codename) {

            const itm = linkedItem as NavigationItemModel;

            if (itm.elements.page.value.length > 0) {
              const pageCodename = itm.elements.page.value[0]
              //@ts-ignore
              url = state.getUrlByCodename(pageCodename)
            } else {
              //@ts-ignore
              url = linkedItem.elements.externalUrl.value
            }


          }
          else {
            const itm = linkedItem as NavigationSectionModel;

            if (itm.elements.landingPage.value.length > 0) {
              const pageCodename = itm.elements.landingPage.value[0]
              //@ts-ignore
              url = state.getUrlByCodename(pageCodename)
            } else {
              //@ts-ignore
              url = linkedItem.elements.externalUrl.value
            }
          }

          navigationItems.push({
            title: linkedItem.elements.title.value,
            url: url,
            subItems: getSubItems(linkedItem)
          } as NavigationItem)
        }

        function getSubItems(this: any, item: any) {
          let subItems: NavigationItem[] = []

          if (item.elements.navigationItems && item.elements.navigationItems.value.length > 0) {
            for (const codename of item.elements.navigationItems.value) {
              const linkedItem = state.linkedItems?.[codename] as NavigationItemModel | NavigationSectionModel
              if (!linkedItem) continue

              let url = ''
              if (linkedItem.system.type == contentTypes.navigation_item.codename) {

                const itm = linkedItem as NavigationItemModel;

                if (itm.elements.page.value.length > 0) {
                  const pageCodename = itm.elements.page.value[0]
                  //@ts-ignore
                  url = state.getUrlByCodename(pageCodename)
                } else {
                  //@ts-ignore
                  url = linkedItem.elements.externalUrl.value
                }


              }
              else {
                const itm = linkedItem as NavigationSectionModel;

                if (itm.elements.landingPage.value.length > 0) {
                  const pageCodename = itm.elements.landingPage.value[0]
                  //@ts-ignore
                  url = state.getUrlByCodename(pageCodename)
                } else {
                  //@ts-ignore
                  url = linkedItem.elements.externalUrl.value
                }
              }

              subItems.push({
                title: linkedItem.elements.title.value,
                url: url,
                subItems: getSubItems(linkedItem)
              } as NavigationItem)
            }
          }
          return subItems
        }
      }
      return navigationItems
    },
    getFooterNavigationItems: (state) => (): NavigationItem[] => {
      const navigationItems: NavigationItem[] = []
      if (state.fetched && state.site) {
        const footerCodename = state.site?.elements?.navigationFooter?.value?.[0]
        if (!footerCodename) return []
        const navigation = state.linkedItems?.[footerCodename] as NavigationSectionModel | undefined
        if (!navigation) return []

        for (const item of navigation?.elements?.navigationItems?.value ?? []) {
          const linkedItem = state.linkedItems?.[item] as NavigationItemModel | NavigationSectionModel
          if (!linkedItem) continue

          let url = ''

          if (linkedItem.system.type == contentTypes.navigation_item.codename) {

            const itm = linkedItem as NavigationItemModel;

            if (itm.elements.page.value.length > 0) {
              const pageCodename = itm.elements.page.value[0]
              //@ts-ignore
              url = state.getUrlByCodename(pageCodename)
            } else {
              //@ts-ignore
              url = linkedItem.elements.externalUrl.value
            }


          }
          else {
            const itm = linkedItem as NavigationSectionModel;

            if (itm.elements.landingPage.value.length > 0) {
              const pageCodename = itm.elements.landingPage.value[0]
              //@ts-ignore
              url = state.getUrlByCodename(pageCodename)
            } else {
              //@ts-ignore
              url = linkedItem.elements.externalUrl.value
            }
          }
          navigationItems.push({
            title: linkedItem.elements.title.value,
            url: url,
            subItems: getSubItems(linkedItem)
          } as NavigationItem)
        }

        function getSubItems(item: any) {
          let subItems: NavigationItem[] = []

          if (item.elements.navigationItems && item.elements.navigationItems.value.length > 0) {
            for (const codename of item.elements.navigationItems.value) {
              const linkedItem = state.linkedItems?.[codename] as NavigationItemModel|NavigationSectionModel
              if (!linkedItem) continue

              let url = ''
              if (linkedItem.system.type == contentTypes.navigation_item.codename) {

                const itm = linkedItem as NavigationItemModel;

                if (itm.elements.page.value.length > 0) {
                  const pageCodename = itm.elements.page.value[0]
                  //@ts-ignore
                  url = state.getUrlByCodename(pageCodename)
                } else {
                  //@ts-ignore
                  url = linkedItem.elements.externalUrl.value
                }


              }
              else {
                const itm = linkedItem as NavigationSectionModel;

                if (itm.elements.landingPage.value.length > 0) {
                  const pageCodename = itm.elements.landingPage.value[0]
                  //@ts-ignore
                  url = state.getUrlByCodename(pageCodename)
                } else {
                  //@ts-ignore
                  url = linkedItem.elements.externalUrl.value
                }
              }
              subItems.push({
                title: linkedItem.elements.title.value,
                url: url,
                subItems: getSubItems(linkedItem)
              } as NavigationItem)
            }
          }
          return subItems
        }
      }

      return navigationItems
    }
  }
})
